import { clearAction, updateAction } from '@/quote/util/update-action';
import {
  REQUEST_SUCCESS_CODE,
  SimpleRevisionTypeEnums,
  apiGetQuotePromotionAllReleatedDetailInformation,
  apiGetQuotePromotionMemberList,
  apiGetTripartiteCooperationDetailInfoWithNoAuth,
  apiGetTripartiteCooperationProductDetailInfoWithNoAuth,
} from '@jove-frontend/uikit';
import { useStateMachine } from 'little-state-machine';
import { isArray } from 'lodash';

export const useQuotePromotion = () => {
  const { getState, actions } = useStateMachine({ updateAction });

  /**
   * requests
   */
  const requestQuotePromotionDetailInformation = async (config: { promotionId: string }) => {
    const [[promotionRes], [memberListRes]] = await Promise.all([
      apiGetTripartiteCooperationProductDetailInfoWithNoAuth({ productId: config?.promotionId }),
      apiGetQuotePromotionMemberList({ promotionId: config?.promotionId }),
    ]);

    const { firstName, lastName, email, phone } = memberListRes?.data?.[0] || {};
    let promotionInfo = {
      ...promotionRes?.data,
      id: config?.promotionId,
      defaultBrokerName: firstName ? `${firstName} ${lastName}` : '',
      defaultBrokerEmail: email || '',
      defaultBrokerPhone: phone || '',
      regions:
        isArray(promotionRes?.data?.regions) && promotionRes?.data?.regions?.length > 0
          ? promotionRes.data.regions
          : promotionRes?.data?.region
          ? [promotionRes?.data?.region]
          : [], // Compatible with old data
    };
    if (promotionRes?.code === REQUEST_SUCCESS_CODE) {
      if (promotionInfo?.partnerId) {
        const [partnerRes] = await apiGetTripartiteCooperationDetailInfoWithNoAuth({ tripartiteId: promotionInfo.partnerId });

        if (partnerRes?.code === REQUEST_SUCCESS_CODE) {
          const promotionHaveOwnFile = (data) => {
            return (
              (data?.revision_type === SimpleRevisionTypeEnums.FILE_URL && !!data?.file_url) ||
              (data?.revision_type !== SimpleRevisionTypeEnums.FILE_URL && !!data?.id)
            );
          };

          const { quote_terms_document, privacy_policy_document } = promotionInfo.payload || {};
          promotionInfo = {
            ...promotionInfo,
            logo: partnerRes.data?.logo,
            payload: {
              ...promotionInfo.payload,
              quote_terms_document: promotionHaveOwnFile(quote_terms_document)
                ? quote_terms_document
                : partnerRes.data?.website_configuration?.quote_terms_document || null,
              privacy_policy_document: promotionHaveOwnFile(privacy_policy_document)
                ? privacy_policy_document
                : partnerRes.data?.website_configuration?.privacy_policy_document || null,
            },
            brokerName: partnerRes?.data?.name,
            partnerParentId: partnerRes?.data?.parent_id,
            subBrokerType: partnerRes?.data?.sub_broker_type,
            // Partner level payload, it carries the broker branding settings (e.g. the quote header icon)
            partnerPayload: partnerRes?.data?.payload,
          };
        }
      }
    }

    return promotionInfo;
  };

  const requestQuotePromotionDetailInformationByQuoteId = async (config: { promotionId: string; quoteId: string }) => {
    const [promotionRes] = await apiGetQuotePromotionAllReleatedDetailInformation({ quoteId: config?.quoteId });
    let oneMemberItem = null;
    if (config?.promotionId) {
      const [memberListRes] = await apiGetQuotePromotionMemberList({ promotionId: config.promotionId });
      oneMemberItem = memberListRes?.data?.[0];
    }

    const { firstName, lastName, email, phone } = oneMemberItem || {};
    const { memberFirstName, memberLastName, memberEmail, memberPhone, brokerFirstName, brokerLastName, brokerEmail, brokerPhone } =
      promotionRes?.data || {};
    let promotionInfo = {
      ...promotionRes?.data,
      id: config?.promotionId,
      defaultBrokerName: memberFirstName
        ? `${memberFirstName} ${memberLastName}`
        : brokerFirstName
        ? `${brokerFirstName} ${brokerLastName}`
        : firstName
        ? `${firstName} ${lastName}`
        : '',
      defaultBrokerEmail: memberEmail || brokerEmail || email || '',
      defaultBrokerPhone: memberPhone || brokerPhone || phone || '',
      logo: {
        ...promotionRes?.data?.partnerLogo,
      },
      payload: promotionRes?.data?.promotionPayload,
    };
    return promotionInfo;
  };

  const updateQuotePromotionStoreInfo = async (config: { promotionId: string; quoteId: string }) => {
    const promotionInfo = await requestQuotePromotionDetailInformationByQuoteId(config);
    if (promotionInfo) {
      actions.updateAction({
        ...getState(),
        promotionInfo,
      });
    }
  };

  return {
    requestQuotePromotionDetailInformation,
    requestQuotePromotionDetailInformationByQuoteId,
    updateQuotePromotionStoreInfo,
  };
};
